import React, { useEffect, useState } from "react";

import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ls from "localstorage-slim";
import { Col, Container, Image, Row } from "react-bootstrap";
import "react-image-gallery/styles/css/image-gallery.css";
import { NavLink, useSearchParams } from "react-router-dom";

import { getCurrentLang, getUrlLangPrefix } from "@/utils/Local";
import { calculateReadTime, formatDate } from "@/utils/Toolbox";

import FaClock from "../assets/icons/faClock.svg";
import BreadcrumbComponent, {
  buildBreadcrumbJsonLd,
} from "../components/Breadcrumb.component";
import DocumentComponent from "../components/Document.component";
import ErrorComponent from "../components/Error.component";
import LoaderComponent from "../components/Loader.component";
import ScheduleComponent from "../components/Schedule.component";
import i18n from "../i18n";
import apiService from "../services/api";

import type { IBlog } from "@/interfaces/IBlog";

const Blog = (): React.JSX.Element => {
  const { t } = i18n;
  const lang = getCurrentLang();
  const [searchParams] = useSearchParams();
  const blogId = (searchParams.get("id") ?? "").toLowerCase();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blog, setBlog] = useState<IBlog>();
  const langPrefix = getUrlLangPrefix();

  const fetchBlog = async () => {
    try {
      const cacheBlogKey = `blog:${blogId}#`;

      const cachedBlog = ls.get(cacheBlogKey, { decrypt: true }) as IBlog;
      if (cachedBlog) {
        setBlog(cachedBlog);
      } else {
        const response = await apiService.fetchBlog(blogId);
        if (response.status === 200 && response.data?.blog) {
          const blogData = response.data.blog as IBlog;
          ls.set(cacheBlogKey, blogData, { ttl: 1810, encrypt: true });
          setBlog(blogData);
        }
      }
    } catch (err: any) {
      if (err.response?.status && err.response.status === 404) {
        // not found
      } else {
        setError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogId]);

  const cleanTitle = blog
    ? blog.title[lang].replaceAll("?", "").replaceAll(" ", "_")
    : "";

  const blogLink = (blogObject: IBlog) =>
    `${langPrefix}/${t("_route.blog")}/${cleanTitle}?id=${blogObject.id}`;

  const breadcrumbs = blog
    ? [
        { label: t("blog"), to: `${langPrefix}/${t("_route.blog")}` },
        { label: blog.title[lang] },
      ]
    : [];

  return (
    <React.Fragment>
      {blog ? (
        <DocumentComponent
          jsonLd={buildBreadcrumbJsonLd(breadcrumbs)}
          title={`${t("blog")} > ${blog.title[lang]}`}
          og={{
            description: cleanTitle,
            image: {
              src: blog.image,
              type: "image/jpg",
              width: 800,
              height: 450,
            },
          }}
        />
      ) : null}

      {isLoading ? <LoaderComponent /> : null}

      {error ? <ErrorComponent /> : null}

      {!isLoading && !error && !blog && "blog not found"}

      {blog ? (
        <React.Fragment>
          <BreadcrumbComponent items={breadcrumbs} />

          <Container className="mb-4 blog-container">
            <Row>
              <Col className="mt-5 mb-3" lg={{ span: 10, offset: 1 }} xs={12}>
                <Row>
                  <Col className="mb-4" xs={12}>
                    <h1>{blog.title[lang]}</h1>
                  </Col>
                  <Col className="mb-5" xs={12}>
                    <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center  gap-3">
                      <div className="d-flex align-items-center justify-content-between gap-1 author-container">
                        <div className="text-center author-avatar">
                          <Image
                            rounded
                            alt="PakVille™"
                            src="/images/logo/PakVille-vrt.png"
                          />
                        </div>
                        {`${t("published_by")}: ${blog.author}`}
                      </div>

                      <span className="d-none d-lg-block">|</span>

                      <span>
                        {`${t("posted_on")} ${formatDate(blog.date, lang)}`}
                      </span>

                      <span className="d-none d-lg-block">|</span>

                      <span>
                        <div className="d-flex align-items-center gap-1">
                          <img alt={t(`blog-read-time`)} src={FaClock} />

                          <span>
                            {`${calculateReadTime(blog.content[lang])} ${t("min")}`}
                          </span>
                          <span>{t("read")}</span>
                        </div>
                      </span>
                    </div>
                  </Col>

                  <Col className="mb-4" xs={12}>
                    <Image
                      rounded
                      alt={blog.title[lang]}
                      className="mw-100"
                      src={blog.image}
                    />
                  </Col>

                  <Col className="mb-4" xs={12}>
                    <div
                      dangerouslySetInnerHTML={{ __html: blog.content[lang] }}
                    />
                  </Col>

                  {blog.related_blog ? (
                    <NavLink to={blogLink(blog.related_blog)}>
                      <Col className="mt-5 mb-5 " xs={12}>
                        <div
                          className="d-flex flex-column flex-lg-row gap-4 p-4"
                          style={{
                            backgroundColor: "#F9F8F8",
                            borderRadius: "20px",
                          }}
                        >
                          <div className="related-blog-img-container">
                            <Image
                              rounded
                              alt={blog.related_blog.title[lang]}
                              className="mw-100"
                              src={blog.related_blog.image}
                            />
                          </div>
                          <div className="d-flex flex-column">
                            <h5>
                              {t("next_article")}:{" "}
                              {blog.related_blog.title[lang]}
                            </h5>
                            <div>
                              {t("read_article")}
                              <FontAwesomeIcon
                                className="ms-2"
                                icon={faArrowRight}
                              />
                            </div>
                          </div>
                        </div>
                      </Col>
                    </NavLink>
                  ) : null}
                </Row>
              </Col>
            </Row>
          </Container>

          <ScheduleComponent />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default Blog;
